import { useEffect, useMemo, useRef } from 'react';
import { useSubscription } from '@apollo/client';
import { listActivePatients, listPacuPrepPostopPatients } from '@/graph/patients';
import { useWebSocketReconnect } from '@/hooks/useWebSocketReconnect';
import type { Patient } from '../types/RoomTabletTypes';

/**
 * Custom hook for subscribing to patient updates.
 * Replaces the legacy graphql() HOC for patient subscriptions.
 *
 * @param roomType - The type of room (PRE_OP, PACU, POST_OP). If omitted, all active patients are returned.
 * @param roomId - Optional room id. When provided together with roomType, patients are filtered to that specific room.
 *                 If omitted, all patients of the roomType are returned (for hospital overview mode or multi-room views).
 * @returns Object containing the filtered patients array, loading state, and error
 */
export function usePatientSubscription(roomType?: string, roomId?: number) {
  // Track WebSocket reconnections for logging and debugging
  const reconnectCount = useWebSocketReconnect();
  const prevReconnectCount = useRef(reconnectCount);

  // Log reconnection events for debugging production issues
  useEffect(() => {
    if (reconnectCount > 0 && reconnectCount !== prevReconnectCount.current) {
      console.log(
        `[PatientSubscription] WebSocket reconnected (count: ${reconnectCount}), subscription will auto-resubscribe`
      );
      prevReconnectCount.current = reconnectCount;
    }
  }, [reconnectCount]);

  // Only pass roomId if it's a valid number - otherwise fetch all patients for the roomType
  // This matches the legacy behavior where roomId was optional and omitting it returned all patients
  const normalizedRoomId = typeof roomId === 'number' && !Number.isNaN(roomId) ? roomId : undefined;

  // shouldResubscribe: true ensures subscription re-establishes after WebSocket reconnection
  const { data, loading, error } = useSubscription<{ patients: Patient[] }>(
    roomType ? listPacuPrepPostopPatients : listActivePatients,
    roomType
      ? {
          variables: {
            roomType,
            // Only include roomId if we want to filter to a specific room
            // Omitting roomId returns all patients for the roomType (legacy behavior)
            ...(normalizedRoomId !== undefined && { roomId: normalizedRoomId }),
          },
          shouldResubscribe: true,
        }
      : { shouldResubscribe: true }
  );

  const patients = useMemo(() => data?.patients || [], [data]);

  return {
    patients,
    loading,
    error,
  };
}
